import { modEnter, modK } from '../../lib/platform'
import { useEffect, useId, useRef, useState, type CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'

import type { ISODate } from '@app/shared'
import { chipEnter } from '../../lib/motion'
import { useCaptureQuick, useClipPreview, useClock, type CaptureType } from '../../lib/data'
import { RedditFace } from '../interests/icons'
import { Mic, PlusThin } from '../chat/icons'
import { CAPTURE_TYPES, captureTypeLabel, detect } from './detect'

// ============================================================================
// THE ⌘K CAPTURE PALETTE — LifeOS Chat.dc.html, option 11c.
//
// README §11: "An **overlay, not a route.** The screen behind stays in place,
// blurred `1.5px` under `rgba(16,24,29,.5)`. Centred dialog: 700px, radius 18,
// shadow `0 28px 70px rgba(8,12,20,.4)`. One large input at 400 21px with a mic
// button. No labels, no required fields."
//
// AN OVERLAY, NOT A ROUTE, is the whole design: the route behind does not change,
// so `esc` puts you back exactly where you were, mid-scroll and mid-sentence. That
// is why this mounts in AppShell and why the URL is never touched.
//
// THE INPUT IS UNCONTROLLED. React never owns the value, so the browser's native
// undo stack survives a detection re-render on every keystroke — the journal
// composer's rule, and the one thing no gate in this repo can see. The detection
// runs off the DOM value on change; nothing is ever written back into the field.
//
// SAVING IS LIVE, in demo mode and against the real API: a link becomes a save in
// the Interests inbox, and anything else becomes a row in the Tasks Inbox — the
// palette then says which, in words, rather than closing and leaving you to trust
// it. `POST /api/capture/quick` is the single door for both (lib/data/remote.ts).
// ============================================================================

export function CapturePalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const inputId = useId()
  const input = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const { data: clock } = useClock()
  const capture = useCaptureQuick()

  const [text, setText] = useState('')
  const [override, setOverride] = useState<CaptureType | null>(null)
  const [touched, setTouched] = useState(false)
  const [saved, setSaved] = useState<{ destination: string; href: string } | null>(null)

  // Detection runs on the DOM value, which `text` mirrors. `touched` is what makes the chip
  // OVERRIDABLE: once you pick one it stops moving under you, which is the difference between a
  // suggestion and a fight.
  const detection = detect(text, clock?.date)
  const activeType = touched ? override : detection.type

  const preview = useClipPreview(detection.type === 'clip' ? text.trim() : undefined)

  // Autofocus on open, and reset every time so a palette never opens holding yesterday's draft.
  useEffect(() => {
    if (!open) return
    setText('')
    setOverride(null)
    setTouched(false)
    setSaved(null)
    capture.reset()
    if (input.current) {
      input.current.value = ''
      input.current.focus()
    }
    // `capture` is a stable mutation object from react-query; including it would re-run this on
    // every mutation state change and blow away the field mid-save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open) return null

  function save(andOpen: boolean) {
    const value = input.current?.value.trim() ?? ''
    if (!value) return
    capture.mutate(
      { text: value, type: activeType, due: detection.due as ISODate | null },
      {
        onSuccess: (result) => {
          setSaved({ destination: result.destination, href: result.href })
          setText('')
          setTouched(false)
          setOverride(null)
          if (input.current) input.current.value = ''
          if (andOpen) {
            onClose()
            navigate(result.href)
          }
        },
      },
    )
  }

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-[14px] px-10"
      role="dialog"
      aria-modal="true"
      aria-label="Capture anything"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation()
          onClose()
        }
      }}
    >
      {/* The scrim. A button so a click outside closes, and so the escape hatch is reachable by
          keyboard for anyone who tabs into it. */}
      <button
        type="button"
        aria-label="Close capture"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-scrim"
      />

      <form
        onSubmit={(e) => {
          e.preventDefault()
          save(false)
        }}
        // 13a's panel entrance: `scale(.94) translateY(-8px)` → rest, 180ms on --ease-ink.
        className="mo-palette-enter relative w-[700px] overflow-hidden rounded-dialog bg-surface shadow-overlay"
      >
        <div className="flex items-center gap-[15px] px-[22px] pb-[18px] pt-5">
          <PlusThin className="flex-none text-muted" />
          <label htmlFor={inputId} className="sr-only">
            Capture anything — no required fields
          </label>
          <input
            id={inputId}
            ref={input}
            type="text"
            autoComplete="off"
            placeholder="Capture anything…"
            onChange={(e) => setText(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                save(true)
              }
            }}
            className="min-w-0 flex-1 bg-transparent text-[21px] font-normal leading-[1.2] tracking-[-0.01em] text-ink outline-none placeholder:text-microcopy"
          />
          <button
            type="button"
            aria-disabled="true"
            title="Capturing by voice arrives with V1, the transcription system — the same desktop worker that turns a lecture recording into a transcript. Typing works now."
            onClick={(e) => e.preventDefault()}
            className="flex size-[34px] flex-none cursor-not-allowed items-center justify-center rounded-row bg-fill-2 text-ink-3"
          >
            <Mic size={16} />
            <span className="sr-only">Capture by voice</span>
          </button>
        </div>

        <div className="flex flex-col gap-[13px] px-[22px] pb-4">
          {/* THE DETECTION, SHOWN NOT ASKED. A pasted link renders its preview immediately. */}
          {detection.type === 'clip' && preview.data && (
            <div className="flex items-center gap-3 rounded-preview border border-orange-line bg-orange-fill px-[14px] py-3">
              <span
                className="flex size-[26px] flex-none items-center justify-center rounded-tile-sm border border-orange-line bg-surface text-orange-2"
                aria-hidden
              >
                <RedditFace size={14} strokeWidth={1.9} />
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="truncate text-[12.5px] font-semibold leading-[1.3]">
                  {/* Null until something has actually read the page. A title guessed from a URL
                      slug would be the app inventing a headline. */}
                  {preview.data.title ?? preview.data.url}
                </span>
                <span className="text-[10.5px] font-normal leading-none text-microcopy">
                  {preview.data.sourceDetail} · link detected · will file under{' '}
                  {preview.data.filesUnder}
                  {preview.data.title === null && ' · title arrives when the capture endpoint fetches it (V5)'}
                </span>
              </span>
              <span className="flex-none rounded-pill border border-orange-line bg-surface px-2 py-[3px] text-[9.5px] font-semibold leading-[1.6] text-orange">
                CLIP
              </span>
            </div>
          )}

          {/* 13a · THE PARSE RESULT ANIMATES IN BENEATH THE INPUT, one chip at a time (~80ms
              apart), as the parser commits — so you watch the system settle on an interpretation
              before you press enter.

              THE RULE IS THE ABSENCE: "if a field can't be parsed, NO CHIP APPEARS. The absence
              is the feedback — never a placeholder chip, never a guess." `detect()` resolves a
              date only when a real phrase matched, so a capture with no date phrase renders no
              date chip at all rather than a grey "no date" one. That is also why this row is not
              a fixed set of slots. */}
          {detection.type !== null && detection.type !== 'clip' && !touched && (
            <ParseChips
              // Re-keyed on the parse itself: a new interpretation is a new commit, and it earns
              // the stagger again. An unchanged parse (you typed another word that changed
              // nothing) does not re-animate.
              key={`${detection.type}-${detection.due ?? ''}-${detection.because ?? ''}`}
              chips={[
                // The resolved datetime FIRST, per the brief's order — it is the claim with the
                // most consequence, and the one most worth catching before you commit.
                ...(detection.due
                  ? [{ text: detection.due, tone: 'bg-accent-fill text-accent' }]
                  : []),
                {
                  text: `${captureTypeLabel(detection.type)} · ${detection.because}`,
                  tone: 'bg-info-fill text-info',
                },
              ]}
              tail="Change it below, or leave it."
            />
          )}

          <div className="flex items-center gap-2">
            <span className="flex-none text-[9.5px] font-semibold leading-none tracking-[0.08em] text-microcopy">
              TYPE
            </span>
            {CAPTURE_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                aria-pressed={activeType === t}
                onClick={() => {
                  setTouched(true)
                  setOverride(activeType === t ? null : t)
                }}
                style={{ '--mo-rule': 'var(--color-school)' } as CSSProperties}
                className={clsx(
                  // 12d: a fully-rounded shape, so the ink FILLS from the left rather than ruling
                  // the edge. Replaces the flat `hover:bg-surface-2` step this row used to take.
                  'mo-ink flex-none rounded-pill px-3 py-[5px] leading-none',
                  activeType === t
                    ? 'bg-ink text-[11.5px] font-semibold text-surface'
                    : 'border border-line bg-surface text-[11.5px] font-medium text-ink-3',
                )}
              >
                {captureTypeLabel(t)}
              </button>
            ))}
            <span className="ml-auto flex-none text-[10.5px] font-normal leading-none text-microcopy">
              optional — unset goes to Inbox
            </span>
          </div>

          {saved && (
            <p role="status" className="text-[11px] leading-[1.45] text-ink-3">
              Saved to <strong className="font-semibold text-ink">{saved.destination}</strong>. It
              is there now —{' '}
              <button
                type="button"
                onClick={() => {
                  onClose()
                  navigate(saved.href)
                }}
                className="font-medium text-ink underline underline-offset-2 transition-colors duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:text-ink-3"
              >
                open it
              </button>
              . Keep typing to capture another.
            </p>
          )}

          {capture.error && (
            <p role="status" className="text-[11px] leading-[1.45] text-danger text-pretty">
              {capture.error instanceof Error
                ? capture.error.message
                : 'That could not be saved, and the reason did not come back with a sentence attached.'}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-fill bg-surface-2 px-[22px] py-3">
          <Keycap cap="⏎" label="save" />
          <Keycap cap="esc" label="close" />
          <Keycap cap={modEnter} label="save and open" />
          <span className="ml-auto text-[10.5px] font-normal leading-none text-microcopy">
            no required fields · nothing else to fill in
          </span>
        </div>
      </form>

      <p className="relative text-[11.5px] font-normal leading-none text-caption">
        {modK} from anywhere · the screen behind you keeps its place
      </p>
    </div>
  )
}

/**
 * 13a's chip row. One chip at a time, `CHIP_STAGGER_MS` apart, in the order the parser committed
 * to them. Under `prefers-reduced-motion` every chip is simply there — the stagger is the part
 * that goes, never the result.
 */
function ParseChips({
  chips,
  tail,
}: {
  chips: Array<{ text: string; tone: string }>
  tail: string
}) {
  const row = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    const nodes = row.current?.querySelectorAll<HTMLElement>('[data-chip]')
    if (!nodes) return
    const runs = [...nodes].map((node, i) => chipEnter(node, i))
    return () => {
      for (const run of runs) run.cancel()
    }
  }, [chips])

  return (
    <p ref={row} className="flex flex-wrap items-center gap-[7px] text-[10.5px] leading-none">
      {chips.map((chip) => (
        <span
          key={chip.text}
          data-chip
          className={`flex-none rounded-pill px-[9px] py-1 text-[10.5px] font-semibold leading-[1.4] opacity-0 ${chip.tone}`}
        >
          {chip.text}
        </span>
      ))}
      <span className="text-microcopy">{tail}</span>
    </p>
  )
}

function Keycap({ cap, label }: { cap: string; label: string }) {
  return (
    <span className="flex items-center gap-[7px]">
      <kbd className="rounded-keycap border border-line-2 bg-surface px-2 py-[3px] text-[10px] font-semibold not-italic leading-[1.5] text-ink-2">
        {cap}
      </kbd>
      <span className="text-[11px] font-normal leading-none text-muted">{label}</span>
    </span>
  )
}
